import { replace } from 'connected-react-router'
import React, { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import EditModalFooter from './EditModalFooter'
import { UpdateCruiseItemViewModel, UpdateTourItemViewModel } from './models'
import {
  extractEditableEndDateTimeStrings,
  extractEditableStartDateTimeStrings,
  updateCruisePayloadFromViewModel,
  updateTourPayloadFromViewModel,
} from './utils'
import { formOptions } from '../Common/Fields/config'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { useAppDispatch } from 'hooks/reduxHooks'
import CruiseFields from 'tripPlanner/components/TripModal/TripItems/Common/CruiseFields'
import TourFields from 'tripPlanner/components/TripModal/TripItems/Common/TourFields'
import DeleteTripItem from 'tripPlanner/components/TripModal/TripItems/DeleteTripItem'
import { useUpdateTripItem } from 'tripPlanner/hooks/api'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { CruiseItem, TourItem, TripItem } from 'tripPlanner/types/tripItem'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

type ItemType = TourItem | CruiseItem

interface Props {
  item: ItemType
}

type ViewModel = UpdateTourItemViewModel | UpdateCruiseItemViewModel

const itemToView = (item: ItemType) => {
  const {
    id,
    tripId,
    createdAt,
    updatedAt,
    userUpdatedAt,
    createdBy,
    updatedBy,
    startUtcDateTime,
    endUtcDateTime,
    sortableName,
    ...editableFields
  } = item

  return {
    ...editableFields,
    ...extractEditableStartDateTimeStrings(item),
    ...extractEditableEndDateTimeStrings(item),
    isEndPlaceSame: false,
  }
}

function EditTourAndCruises(props: Props) {
  const { item } = props
  const dispatch = useAppDispatch()

  const openModal = useOpenModal()
  const { closeModal } = useModalCallbacks()

  const methods = useForm<ViewModel>({
    ...formOptions,
    defaultValues: itemToView(item),
  })
  const { handleSubmit } = methods

  const { mutate: updateMutate, isLoading } = useUpdateTripItem({
    onSuccess: ({ id }: TripItem) => {
      dispatch(replace({ hash: itemSectionId(id) }))
      closeModal()
    },
  })

  const deleteItem = useCallback(
    () => openModal(DeleteTripItem, { item }, { height: 'auto' }),
    [openModal, item],
  )

  const updateItem = useCallback(
    ({
      isEndPlaceSame,
      ...viewModel
    }: UpdateTourItemViewModel | UpdateCruiseItemViewModel) => {
      switch (viewModel.type) {
        case 'TOUR':
          updateMutate({
            tripId: item.tripId,
            tripItemId: item.id,
            tripItem: updateTourPayloadFromViewModel(viewModel),
          })
          break
        case 'CRUISE':
          updateMutate({
            tripId: item.tripId,
            tripItemId: item.id,
            tripItem: updateCruisePayloadFromViewModel(viewModel),
          })
          break
      }
    },
    [item, updateMutate],
  )

  return (
    <>
      <ModalHeader
        title="Edit Tour or Cruise"
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <FormProvider {...methods}>
          <ModalContent>
            {item.type === 'TOUR' && <TourFields tripId={item.tripId} />}
            {item.type === 'CRUISE' && <CruiseFields tripId={item.tripId} />}
          </ModalContent>
        </FormProvider>
      </ModalBody>
      <EditModalFooter
        deleteItem={deleteItem}
        onSubmit={handleSubmit(updateItem)}
        isLoading={isLoading}
      />
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default EditTourAndCruises
